import { useState, useEffect } from 'react';
import { BASE_URL } from 'utils/axios';

/**
 * Custom hook for fetching documents associated with a travel plan
 *
 * @param {Object} params - Parameters for the hook
 * @param {Object} params.travelPlanId - The ID of the travel plan
 * @param {Object} params.token - Authentication token
 * @param {number} [params.page=0] - Page number for pagination
 * @param {number} [params.size=100] - Page size for pagination
 * @returns {Object} - State and data related to document fetching
 */
const useFetchDocuments = ({ travelPlanId, token, page = 0, size = 100 }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDocuments = async () => {
            if (!travelPlanId || !token) return;

            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`${BASE_URL}/api/travel-plans/${travelPlanId}/documents?page=${page}&size=${size}`, {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Request failed: ${response.status}`);
                }

                const data = await response.json();
                setDocuments(data.content || []);
            } catch (error) {
                console.error('Error fetching documents:', error);
                setError(error.message || 'Failed to fetch documents');
                setDocuments([]);
            } finally {
                setLoading(false);
            }
        };

        fetchDocuments();
    }, [travelPlanId, token, page, size]);

    return { documents, loading, error };
};

export default useFetchDocuments;
